import { useEffect, useMemo, useState } from 'react'
import {
  FiEdit2,
  FiFlag,
  FiMessageCircle,
  FiMoreHorizontal,
  FiSend,
  FiThumbsUp,
  FiTrash2,
  FiUserX,
  FiX,
} from 'react-icons/fi'
import { useLocation } from 'react-router-dom'
import { AnalyticsFeature } from '@domain/features/analytics/analytics-feature'
import { getActivityComment } from '@domain/features/activity-comments/activity-comments-feature'
import {
  NOTIFICATION_COMMENT_QUERY_PARAM,
  NOTIFICATION_REPLY_QUERY_PARAM,
  createCommentAddedNotification,
  createCommentLikedNotification,
  createCommentReplyNotification,
  createReplyLikedNotification,
} from '@domain/features/user-notifications/user-notifications-feature'
import type {
  ActivityComment,
  CommentsOnType,
  CommentReply,
} from '@domain/models/activity-comments'
import { CommentsOn } from '@domain/models/activity-comments'
import { useActivityCommentsActions } from '@presentation/hooks/useActivityCommentsActions'
import { useActivityExploreComments } from '@presentation/hooks/useActivityExploreComments'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { useCommentReplies } from '@presentation/hooks/useCommentReplies'
import { useUserDisplayNames } from '@presentation/hooks/useUserDisplayNames'

interface CommentsSectionProps {
  activityExploreId?: string
  targetId?: string
  commentsOn?: CommentsOnType
  symptomId?: string
  onAuthRequired?: () => void
  contentOwnerId?: string
}

interface CommentCardProps {
  comment: ActivityComment
  currentUserId: string | undefined
  resolveDisplayName: (userId: string | undefined) => string
  onLike: (comment: ActivityComment) => void
  onReply?: (comment: ActivityComment) => void
  onEdit: (comment: ActivityComment) => void
  onDelete: (comment: ActivityComment) => void
  onReport: () => void
  onBlock: () => void
  showReplyButton?: boolean
  showReplyCount?: boolean
}

interface ReplyRowProps {
  commentId: string
  reply: CommentReply
  highlighted?: boolean
  currentUserId: string | undefined
  resolveDisplayName: (userId: string | undefined) => string
  onLike: (commentId: string, reply: CommentReply) => void
  onEdit: (commentId: string, reply: CommentReply) => void
  onDelete: (commentId: string, reply: CommentReply) => void
  onReport: () => void
  onBlock: () => void
}

interface EditTextModalProps {
  title: string
  initialValue: string
  submitting: boolean
  onClose: () => void
  onSubmit: (value: string) => Promise<void>
}

interface CommentRepliesDialogProps {
  open: boolean
  comment: ActivityComment | null
  highlightedReplyId?: string
  commentsOn: CommentsOnType
  currentUserId: string | undefined
  currentUserSlug?: string | null
  resolveDisplayName: (userId: string | undefined) => string
  onClose: () => void
  onAuthRequired?: () => void
}

const COMMENT_TEXTAREA_CLASS = 'w-full rounded-xl border border-border-light bg-surface px-4 py-3 text-base font-semibold text-text-primary shadow-sm transition-colors placeholder:text-text-muted focus:border-blue! focus:outline-none! focus:ring-0! focus-visible:rounded-xl! focus-visible:border-blue! focus-visible:outline-none! focus-visible:outline-offset-0! focus-visible:ring-0!'

function formatDate(value: Date | undefined) {
  if (!value) {
    return ''
  }
  return value.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function isLikedByCurrentUser(
  likedBy: string[] | undefined,
  currentUserId: string | undefined,
) {
  if (!currentUserId) {
    return false
  }
  return Boolean(likedBy?.includes(currentUserId))
}

function isCommentLinkedToCurrentContext(
  comment: ActivityComment,
  targetId: string,
  commentsOn: CommentsOnType,
  symptomId?: string,
): boolean {
  if (commentsOn === CommentsOn.Symptom) {
    return comment.solutionRef === targetId && comment.pdSymptomId === symptomId
  }

  if (commentsOn === CommentsOn.Webinar) {
    return comment.activityRef === targetId && comment.activityRefCollection === 'webinars'
  }

  if (commentsOn === CommentsOn.Podcast) {
    return comment.activityRef === targetId && comment.activityRefCollection === 'podcast_episodes'
  }

  if (commentsOn === CommentsOn.InstagramReel) {
    return comment.activityRef === targetId && comment.activityRefCollection === 'instagram_reels'
  }

  if (commentsOn === CommentsOn.WebinarSuggestion) {
    return comment.activityRef === targetId && comment.activityRefCollection === 'webinar_suggestions'
  }

  return comment.activityRef === targetId
}

function CommentCard({
  comment,
  currentUserId,
  resolveDisplayName,
  onLike,
  onReply,
  onEdit,
  onDelete,
  onReport,
  onBlock,
  showReplyButton = true,
  showReplyCount = true,
}: CommentCardProps) {
  const [optionsOpen, setOptionsOpen] = useState(false)
  const { count: repliesCount } = useCommentReplies(comment.uid, {
    enabled: showReplyCount,
    descending: false,
  })

  const isOwner = comment.postedBy === currentUserId
  const liked = isLikedByCurrentUser(comment.likedBy, currentUserId)

  return (
    <article className="rounded-xl border border-border-light bg-card-surface p-4 shadow-lg shadow-black/20">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-base font-semibold text-text-primary sm:text-lg">
            {resolveDisplayName(comment.postedBy)}
          </p>
          <p className="text-sm text-text-muted">
            {formatDate(comment.postedOn)}
          </p>
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={() => setOptionsOpen((current) => !current)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-border-light hover:text-text-primary"
            aria-label="Comment options"
          >
            <FiMoreHorizontal className="h-4 w-4" />
          </button>
          {optionsOpen && (
            <div className="absolute right-0 top-9 z-10 min-w-[160px] rounded-lg border border-border-light bg-card-surface p-1 shadow-lg">
              {isOwner ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setOptionsOpen(false)
                      onEdit(comment)
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-base font-semibold text-text-primary transition-colors hover:bg-border-light"
                  >
                    <FiEdit2 className="h-4 w-4" />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOptionsOpen(false)
                      onDelete(comment)
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-base font-semibold text-red-600 transition-colors hover:bg-red-50"
                  >
                    <FiTrash2 className="h-4 w-4" />
                    Delete
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setOptionsOpen(false)
                      onReport()
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-base font-semibold text-text-primary transition-colors hover:bg-border-light"
                  >
                    <FiFlag className="h-4 w-4" />
                    Report
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOptionsOpen(false)
                      onBlock()
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-base font-semibold text-text-primary transition-colors hover:bg-border-light"
                  >
                    <FiUserX className="h-4 w-4" />
                    Block
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <p className="mt-3 whitespace-pre-wrap text-base leading-relaxed text-text-primary sm:text-lg">
        {comment.comment}
      </p>

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => onLike(comment)}
          className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold transition-colors ${
            liked
              ? 'bg-blue text-white'
              : 'bg-border-light text-text-secondary hover:bg-primary/10 hover:text-primary'
          }`}
        >
          <FiThumbsUp className="h-4 w-4" />
          {comment.likes ?? 0}
        </button>
        {showReplyButton && onReply ? (
          <button
            type="button"
            onClick={() => onReply(comment)}
            className="inline-flex items-center gap-2 rounded-full bg-border-light px-3 py-2 text-sm font-semibold text-text-secondary transition-colors hover:bg-primary/10 hover:text-primary"
          >
            <FiMessageCircle className="h-4 w-4" />
            Reply{showReplyCount && repliesCount > 0 ? ` (${repliesCount})` : ''}
          </button>
        ) : null}
      </div>
    </article>
  )
}

function ReplyRow({
  commentId,
  reply,
  highlighted = false,
  currentUserId,
  resolveDisplayName,
  onLike,
  onEdit,
  onDelete,
  onReport,
  onBlock,
}: ReplyRowProps) {
  const [optionsOpen, setOptionsOpen] = useState(false)
  const isOwner = reply.postedBy === currentUserId
  const liked = isLikedByCurrentUser(reply.likedBy, currentUserId)

  return (
    <article
      id={`reply-${reply.uid}`}
      className={`ml-3 rounded-xl border border-border-light border-l-4 border-l-border-light bg-card-surface p-4 pl-5 shadow-lg shadow-black/20 sm:ml-4 sm:pl-6 ${
        highlighted ? 'ring-2 ring-blue/60' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-base font-semibold text-text-primary sm:text-lg">
            {resolveDisplayName(reply.postedBy)}
          </p>
          <p className="text-sm text-text-muted">
            {formatDate(reply.postedOn)}
          </p>
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={() => setOptionsOpen((current) => !current)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-border-light hover:text-text-primary"
            aria-label="Reply options"
          >
            <FiMoreHorizontal className="h-4 w-4" />
          </button>
          {optionsOpen && (
            <div className="absolute right-0 top-9 z-10 min-w-[160px] rounded-lg border border-border-light bg-card-surface p-1 shadow-lg">
              {isOwner ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setOptionsOpen(false)
                      onEdit(commentId, reply)
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-base font-semibold text-text-primary transition-colors hover:bg-border-light"
                  >
                    <FiEdit2 className="h-4 w-4" />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOptionsOpen(false)
                      onDelete(commentId, reply)
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-base font-semibold text-red-600 transition-colors hover:bg-red-50"
                  >
                    <FiTrash2 className="h-4 w-4" />
                    Delete
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setOptionsOpen(false)
                      onReport()
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-base font-semibold text-text-primary transition-colors hover:bg-border-light"
                  >
                    <FiFlag className="h-4 w-4" />
                    Report
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOptionsOpen(false)
                      onBlock()
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-base font-semibold text-text-primary transition-colors hover:bg-border-light"
                  >
                    <FiUserX className="h-4 w-4" />
                    Block
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <p className="mt-3 whitespace-pre-wrap text-base leading-relaxed text-text-primary sm:text-lg">
        {reply.comment}
      </p>

      <div className="mt-4">
        <button
          type="button"
          onClick={() => onLike(commentId, reply)}
          className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold transition-colors ${
            liked
              ? 'bg-blue text-white'
              : 'bg-border-light text-text-secondary hover:bg-primary/10 hover:text-primary'
          }`}
        >
          <FiThumbsUp className="h-4 w-4" />
          {reply.likes ?? 0}
        </button>
      </div>
    </article>
  )
}

function EditTextModal({
  title,
  initialValue,
  submitting,
  onClose,
  onSubmit,
}: EditTextModalProps) {
  const [text, setText] = useState(initialValue)

  const handleSubmit = async () => {
    const nextValue = text.trim()
    if (!nextValue) {
      return
    }
    await onSubmit(nextValue)
  }

  return (
    <div className="fixed inset-0 z-55 bg-black/55 px-4 py-8 sm:px-6">
      <button
        type="button"
        className="absolute inset-0"
        aria-label="Close edit dialog"
        onClick={onClose}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative z-10 mx-auto w-full max-w-xl rounded-2xl bg-surface p-5 shadow-2xl sm:p-6"
      >
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-2xl font-bold text-text-primary">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-border-light hover:text-text-primary"
            aria-label="Close edit dialog"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        <textarea
          value={text}
          onChange={(event) => setText(event.target.value)}
          rows={5}
          className={`mt-4 ${COMMENT_TEXTAREA_CLASS}`}
          placeholder="Write your message..."
        />

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-base font-semibold text-text-secondary transition-colors hover:bg-border-light"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              void handleSubmit()
            }}
            disabled={submitting || text.trim().length === 0}
            className="rounded-lg bg-blue px-4 py-2 text-base font-semibold text-white transition-colors hover:bg-blue/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </section>
    </div>
  )
}

function CommentRepliesDialog({
  open,
  comment,
  highlightedReplyId,
  commentsOn,
  currentUserId,
  currentUserSlug,
  resolveDisplayName: resolveSectionDisplayName,
  onClose,
  onAuthRequired,
}: CommentRepliesDialogProps) {
  const location = useLocation()
  const screenPath = `${location.pathname}${location.search || ''}`
  const actions = useActivityCommentsActions(comment?.activityRef, commentsOn)
  const [replyText, setReplyText] = useState('')
  const [infoMessage, setInfoMessage] = useState<string | null>(null)
  const [editingCommentState, setEditingCommentState] = useState<{
    commentId: string
    initialValue: string
  } | null>(null)
  const [editingReplyState, setEditingReplyState] = useState<{
    replyId: string
    initialValue: string
  } | null>(null)
  const { replies, loading, error } = useCommentReplies(comment?.uid, {
    enabled: open && Boolean(comment?.uid),
    descending: false,
  })
  const replyAuthorIds = (() => {
    const ids = new Set<string>()
    if (comment?.postedBy) {
      ids.add(comment.postedBy)
    }
    replies.forEach((reply) => {
      if (reply.postedBy) {
        ids.add(reply.postedBy)
      }
    })
    return Array.from(ids)
  })()
  const { resolveDisplayName } = useUserDisplayNames(replyAuthorIds)

  useEffect(() => {
    if (!open || !highlightedReplyId) {
      return
    }

    const replyElement = document.getElementById(`reply-${highlightedReplyId}`)
    if (replyElement) {
      replyElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [highlightedReplyId, open, replies.length])

  if (!open || !comment) {
    return null
  }

  const submitReply = async () => {
    const nextReply = replyText.trim()
    if (!nextReply.length) {
      return
    }
    const didCreate = await actions.createCommentReply(
      comment.uid,
      nextReply,
      onAuthRequired,
    )
    if (didCreate) {
      AnalyticsFeature.trackCommentReplySubmit(
        screenPath,
        'comments_reply_dialog',
        commentsOn,
        comment.uid,
      )
      setReplyText('')

      if (comment.postedBy && currentUserId) {
        void createCommentReplyNotification({
          forUserId: comment.postedBy,
          senderId: currentUserId,
          senderSlug: currentUserSlug ?? undefined,
          comment,
        })
      }
    }
  }

  const reportNotImplemented = () => {
    setInfoMessage('Report flow is not connected on web yet.')
  }

  const blockNotImplemented = () => {
    setInfoMessage('Block flow is not connected on web yet.')
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/55 px-4 py-8 sm:px-6">
      <button
        type="button"
        className="absolute inset-0"
        aria-label="Close replies dialog"
        onClick={onClose}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-label="Comment replies"
        className="relative z-10 mx-auto flex h-full w-full max-w-3xl flex-col rounded-2xl bg-background p-4 shadow-2xl sm:p-6"
      >
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-2xl font-bold text-text-primary">
            Replies
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-border-light hover:text-text-primary"
            aria-label="Close replies dialog"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4">
          <CommentCard
            comment={comment}
            currentUserId={currentUserId}
            resolveDisplayName={resolveDisplayName}
            onLike={(nextComment) => {
              void actions.toggleCommentLike(nextComment, onAuthRequired).then((didLike) => {
                if (!didLike || !currentUserId || !nextComment.postedBy) {
                  return
                }
                void createCommentLikedNotification({
                  forUserId: nextComment.postedBy,
                  senderId: currentUserId,
                  senderSlug: currentUserSlug ?? undefined,
                  comment: nextComment,
                })
              })
            }}
            onEdit={(nextComment) => {
              setEditingCommentState({
                commentId: nextComment.uid,
                initialValue: nextComment.comment ?? '',
              })
            }}
            onDelete={(nextComment) => {
              const confirmed = window.confirm(
                'Are you sure you want to delete this comment?',
              )
              if (!confirmed) {
                return
              }
              void actions.deleteComment(nextComment.uid, onAuthRequired).then((didDelete) => {
                if (didDelete) {
                  onClose()
                }
              })
            }}
            onReport={reportNotImplemented}
            onBlock={blockNotImplemented}
            showReplyButton={false}
            showReplyCount={false}
          />
        </div>

        <div className="mt-4 flex-1 overflow-y-auto pr-1">
          {loading ? (
            <p className="text-base font-semibold text-text-secondary">
              Loading replies...
            </p>
          ) : replies.length === 0 ? (
            <p className="text-base font-semibold text-text-secondary">
              No replies yet.
            </p>
          ) : (
            <div className="space-y-3">
              {replies.map((reply) => (
                <ReplyRow
                  key={reply.uid}
                  commentId={comment.uid}
                  reply={reply}
                  highlighted={reply.uid === highlightedReplyId}
                  currentUserId={currentUserId}
                  resolveDisplayName={(userId) => {
                    const resolvedDisplayName = resolveDisplayName(userId)
                    if (
                      resolvedDisplayName !== 'Unknown user'
                      && !resolvedDisplayName.startsWith('User ')
                    ) {
                      return resolvedDisplayName
                    }
                    return resolveSectionDisplayName(userId)
                  }}
                  onLike={(commentId, nextReply) => {
                    void actions.toggleReplyLike(commentId, nextReply, onAuthRequired).then((didLike) => {
                      if (!didLike || !currentUserId || !nextReply.postedBy) {
                        return
                      }
                      void createReplyLikedNotification({
                        forUserId: nextReply.postedBy,
                        senderId: currentUserId,
                        senderSlug: currentUserSlug ?? undefined,
                        comment,
                        replyId: nextReply.uid,
                      })
                    })
                  }}
                  onEdit={(_commentId, nextReply) => {
                    setEditingReplyState({
                      replyId: nextReply.uid,
                      initialValue: nextReply.comment ?? '',
                    })
                  }}
                  onDelete={(commentId, nextReply) => {
                    const confirmed = window.confirm(
                      'Are you sure you want to delete this reply?',
                    )
                    if (!confirmed) {
                      return
                    }
                    void actions.deleteCommentReply(
                      commentId,
                      nextReply.uid,
                      onAuthRequired,
                    )
                  }}
                  onReport={reportNotImplemented}
                  onBlock={blockNotImplemented}
                />
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 border-t border-border-light pt-4">
          <div className="flex items-end gap-2">
            <textarea
              value={replyText}
              onChange={(event) => setReplyText(event.target.value)}
              rows={2}
              placeholder="Write a reply..."
              className={COMMENT_TEXTAREA_CLASS}
            />
            <button
              type="button"
              onClick={() => {
                void submitReply()
              }}
              disabled={actions.mutating || replyText.trim().length === 0}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue text-white transition-colors hover:bg-blue/90 disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Post reply"
            >
              <FiSend className="h-4 w-4" />
            </button>
          </div>
          {(error || actions.error || infoMessage) && (
            <p className="mt-2 text-sm font-semibold text-text-secondary">
              {error ?? actions.error ?? infoMessage}
            </p>
          )}
        </div>

        {editingCommentState && (
          <EditTextModal
            title="Edit comment"
            initialValue={editingCommentState.initialValue}
            submitting={actions.mutating}
            onClose={() => setEditingCommentState(null)}
            onSubmit={async (value) => {
              const didUpdate = await actions.updateComment(
                editingCommentState.commentId,
                value,
                onAuthRequired,
              )
              if (didUpdate) {
                setEditingCommentState(null)
              }
            }}
          />
        )}

        {editingReplyState && (
          <EditTextModal
            title="Edit reply"
            initialValue={editingReplyState.initialValue}
            submitting={actions.mutating}
            onClose={() => setEditingReplyState(null)}
            onSubmit={async (value) => {
              const didUpdate = await actions.updateCommentReply(
                comment.uid,
                editingReplyState.replyId,
                value,
                onAuthRequired,
              )
              if (didUpdate) {
                setEditingReplyState(null)
              }
            }}
          />
        )}
      </section>
    </div>
  )
}

export default function CommentsSection({
  activityExploreId,
  targetId,
  commentsOn = CommentsOn.ActivityExplore,
  symptomId,
  onAuthRequired,
  contentOwnerId,
}: CommentsSectionProps) {
  const location = useLocation()
  const locationSearchParams = new URLSearchParams(location.search)
  const screenPath = `${location.pathname}${location.search || ''}`
  const resolvedTargetId = targetId ?? activityExploreId
  const notificationCommentId =
    locationSearchParams.get(NOTIFICATION_COMMENT_QUERY_PARAM) ?? undefined
  const notificationReplyId =
    locationSearchParams.get(NOTIFICATION_REPLY_QUERY_PARAM) ?? undefined
  const { user } = useAuthUser()
  const [newComment, setNewComment] = useState('')
  const [infoMessage, setInfoMessage] = useState<string | null>(null)
  const [showAllComments, setShowAllComments] = useState(false)
  const [selectedThreadComment, setSelectedThreadComment] = useState<ActivityComment | null>(
    null,
  )
  const [handledDeepLinkKey, setHandledDeepLinkKey] = useState<string | null>(null)
  const [editingCommentState, setEditingCommentState] = useState<{
    commentId: string
    initialValue: string
  } | null>(null)

  const previewComments = useActivityExploreComments(resolvedTargetId, {
    limitCount: 3,
    descending: true,
    commentsOn,
    symptomId,
  })
  const allComments = useActivityExploreComments(resolvedTargetId, {
    enabled: showAllComments,
    descending: false,
    commentsOn,
    symptomId,
  })
  const actions = useActivityCommentsActions(resolvedTargetId, commentsOn, symptomId)

  const visibleComments = useMemo(
    () => previewComments.comments,
    [previewComments.comments],
  )
  const authorIds = (() => {
    const ids = new Set<string>()
    previewComments.comments.forEach((comment) => {
      if (comment.postedBy) {
        ids.add(comment.postedBy)
      }
    })
    allComments.comments.forEach((comment) => {
      if (comment.postedBy) {
        ids.add(comment.postedBy)
      }
    })
    if (selectedThreadComment?.postedBy) {
      ids.add(selectedThreadComment.postedBy)
    }
    return Array.from(ids)
  })()
  const { resolveDisplayName } = useUserDisplayNames(authorIds)

  useEffect(() => {
    if (!resolvedTargetId || !notificationCommentId) {
      return
    }

    const deepLinkKey = [
      commentsOn,
      resolvedTargetId,
      symptomId ?? '-',
      notificationCommentId,
      notificationReplyId ?? '-',
    ].join(':')
    if (handledDeepLinkKey === deepLinkKey) {
      return
    }

    let cancelled = false
    const openThreadFromNotification = async () => {
      let matchingComment = previewComments.comments.find(
        (comment) => comment.uid === notificationCommentId,
      ) ?? allComments.comments.find((comment) => comment.uid === notificationCommentId)

      if (!matchingComment) {
        try {
          matchingComment = await getActivityComment(notificationCommentId)
        } catch {
          if (!cancelled) {
            setHandledDeepLinkKey(deepLinkKey)
          }
          return
        }
      }

      if (
        !matchingComment
        || !isCommentLinkedToCurrentContext(
          matchingComment,
          resolvedTargetId,
          commentsOn,
          symptomId,
        )
      ) {
        if (!cancelled) {
          setHandledDeepLinkKey(deepLinkKey)
        }
        return
      }

      if (!cancelled) {
        setSelectedThreadComment(matchingComment)
        setHandledDeepLinkKey(deepLinkKey)
      }
    }

    void openThreadFromNotification()
    return () => {
      cancelled = true
    }
  }, [
    allComments.comments,
    commentsOn,
    handledDeepLinkKey,
    notificationCommentId,
    notificationReplyId,
    previewComments.comments,
    resolvedTargetId,
    symptomId,
  ])

  const submitComment = async () => {
    const nextComment = newComment.trim()
    if (!nextComment.length) {
      return
    }
    const createdCommentId = await actions.createComment(nextComment, onAuthRequired)
    if (createdCommentId) {
      AnalyticsFeature.trackCommentSubmit(
        screenPath,
        'comments_section',
        commentsOn,
        resolvedTargetId ?? 'unknown_target',
        Boolean(user),
      )
      setNewComment('')

      if (
        commentsOn === CommentsOn.WebinarSuggestion
        && contentOwnerId
        && user?.uid
        && resolvedTargetId
      ) {
        void createCommentAddedNotification({
          forUserId: contentOwnerId,
          senderId: user.uid,
          senderSlug: user.profileSlug ?? undefined,
          comment: {
            uid: createdCommentId,
            activityRef: resolvedTargetId,
            activityRefCollection: 'webinar_suggestions',
          },
        })
      }
    }
  }

  const reportNotImplemented = () => {
    setInfoMessage('Report flow is not connected on web yet.')
  }

  const blockNotImplemented = () => {
    setInfoMessage('Block flow is not connected on web yet.')
  }

  return (
    <section className="mt-10 rounded-2xl border border-border-light bg-background p-4 sm:p-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-2xl font-bold text-text-primary">
          Comments ({previewComments.count})
        </h2>
      </div>

      <div className="mt-4">
        <div className="flex items-end gap-2">
          <textarea
            value={newComment}
            onChange={(event) => setNewComment(event.target.value)}
            rows={3}
            placeholder="Write a comment..."
            className={COMMENT_TEXTAREA_CLASS}
          />
          <button
            type="button"
            onClick={() => {
              void submitComment()
            }}
            disabled={actions.mutating || newComment.trim().length === 0}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue text-white transition-colors hover:bg-blue/90 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Post comment"
          >
            <FiSend className="h-4 w-4" />
          </button>
        </div>

        {(previewComments.error || actions.error || infoMessage) && (
          <p className="mt-2 text-sm font-semibold text-text-secondary">
            {previewComments.error ?? actions.error ?? infoMessage}
          </p>
        )}
      </div>

      <div className="mt-6">
        {previewComments.loading ? (
          <p className="text-base font-semibold text-text-secondary">
            Loading comments...
          </p>
        ) : visibleComments.length === 0 ? (
          <p className="text-base font-semibold text-text-secondary">
            No comments yet. Be the first to share your thoughts.
          </p>
        ) : (
          <div className="space-y-3">
            {visibleComments.map((comment) => (
              <CommentCard
                key={comment.uid}
                comment={comment}
                currentUserId={user?.uid}
                resolveDisplayName={resolveDisplayName}
                onLike={(nextComment) => {
                  void actions.toggleCommentLike(nextComment, onAuthRequired).then((didLike) => {
                    if (!didLike || !user?.uid || !nextComment.postedBy) {
                      return
                    }
                    void createCommentLikedNotification({
                      forUserId: nextComment.postedBy,
                      senderId: user.uid,
                      senderSlug: user.profileSlug ?? undefined,
                      comment: nextComment,
                    })
                  })
                }}
                onReply={(nextComment) => {
                  setSelectedThreadComment(nextComment)
                }}
                onEdit={(nextComment) => {
                  setEditingCommentState({
                    commentId: nextComment.uid,
                    initialValue: nextComment.comment ?? '',
                  })
                }}
                onDelete={(nextComment) => {
                  const confirmed = window.confirm(
                    'Are you sure you want to delete this comment?',
                  )
                  if (!confirmed) {
                    return
                  }
                  void actions.deleteComment(nextComment.uid, onAuthRequired)
                }}
                onReport={reportNotImplemented}
                onBlock={blockNotImplemented}
              />
            ))}
          </div>
        )}
      </div>

      {previewComments.count > 3 && (
        <div className="mt-5">
          <button
            type="button"
            onClick={() => setShowAllComments(true)}
            className="text-base font-semibold text-primary transition-colors hover:text-primary-light"
          >
            See all comments ({previewComments.count})
          </button>
        </div>
      )}

      {showAllComments && (
        <div className="fixed inset-0 z-50 bg-black/55 px-4 py-8 sm:px-6">
          <button
            type="button"
            className="absolute inset-0"
            aria-label="Close all comments dialog"
            onClick={() => setShowAllComments(false)}
          />
          <section
            role="dialog"
            aria-modal="true"
            aria-label="All comments"
            className="relative z-10 mx-auto flex h-full w-full max-w-3xl flex-col rounded-2xl bg-background p-4 shadow-2xl sm:p-6"
          >
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-2xl font-bold text-text-primary">
                All Comments
              </h3>
              <button
                type="button"
                onClick={() => setShowAllComments(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-border-light hover:text-text-primary"
                aria-label="Close all comments dialog"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4 flex-1 overflow-y-auto pr-1">
              {allComments.loading ? (
                <p className="text-base font-semibold text-text-secondary">
                  Loading comments...
                </p>
              ) : allComments.comments.length === 0 ? (
                <p className="text-base font-semibold text-text-secondary">
                  No comments yet.
                </p>
              ) : (
                <div className="space-y-3">
                  {allComments.comments.map((comment) => (
                    <CommentCard
                      key={comment.uid}
                      comment={comment}
                      currentUserId={user?.uid}
                      resolveDisplayName={resolveDisplayName}
                      onLike={(nextComment) => {
                        void actions.toggleCommentLike(nextComment, onAuthRequired).then((didLike) => {
                          if (!didLike || !user?.uid || !nextComment.postedBy) {
                            return
                          }
                          void createCommentLikedNotification({
                            forUserId: nextComment.postedBy,
                            senderId: user.uid,
                            senderSlug: user.profileSlug ?? undefined,
                            comment: nextComment,
                          })
                        })
                      }}
                      onReply={(nextComment) => {
                        setSelectedThreadComment(nextComment)
                      }}
                      onEdit={(nextComment) => {
                        setEditingCommentState({
                          commentId: nextComment.uid,
                          initialValue: nextComment.comment ?? '',
                        })
                      }}
                      onDelete={(nextComment) => {
                        const confirmed = window.confirm(
                          'Are you sure you want to delete this comment?',
                        )
                        if (!confirmed) {
                          return
                        }
                        void actions.deleteComment(nextComment.uid, onAuthRequired)
                      }}
                      onReport={reportNotImplemented}
                      onBlock={blockNotImplemented}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      <CommentRepliesDialog
        open={Boolean(selectedThreadComment)}
        comment={selectedThreadComment}
        highlightedReplyId={notificationReplyId}
        commentsOn={commentsOn}
        currentUserId={user?.uid}
        currentUserSlug={user?.profileSlug}
        resolveDisplayName={resolveDisplayName}
        onClose={() => setSelectedThreadComment(null)}
        onAuthRequired={onAuthRequired}
      />

      {editingCommentState && (
        <EditTextModal
          title="Edit comment"
          initialValue={editingCommentState.initialValue}
          submitting={actions.mutating}
          onClose={() => setEditingCommentState(null)}
          onSubmit={async (value) => {
            const didUpdate = await actions.updateComment(
              editingCommentState.commentId,
              value,
              onAuthRequired,
            )
            if (didUpdate) {
              setEditingCommentState(null)
            }
          }}
        />
      )}
    </section>
  )
}
